import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ParcelForm from '../components/ParcelForm';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { useBillableNavigationLock } from '../hooks/useBillableNavigationLock';
import { shipmentsApi, settingsApi } from '../api/client';
import { getRuntimeApiConfig } from '../api/config';
import { saveRecentOrder } from '../utils/recentOrders';
import { formatCurrency } from '../utils/currency';
import { buildPrintPath } from '../utils/labels';
import {
  canonicalizeRetryApiIdentity,
  LEGACY_UNBOUND_API_IDENTITY,
  resolveStoredBulkApiIdentity,
  retryApiIdentityMatches,
} from '../utils/retryApiIdentity';
import type { Address, AddressRecord, InsuranceTerms, Parcel, Rate, ShipmentPreview, ShipmentResult } from '../types';

const emptyParcel: Parcel = {
  weight: 0,
};

// Delay helper to avoid EasyPost rate limiting
const delay = (ms: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  const timer = window.setTimeout(resolve, ms);
  signal.addEventListener('abort', () => {
    window.clearTimeout(timer);
    reject(new DOMException('Bulk shipment processing cancelled', 'AbortError'));
  }, { once: true });
});

// Rate limit: 1.5 seconds between shipments to avoid hitting EasyPost API limits
const RATE_LIMIT_DELAY_MS = 1500;

interface ShipmentQueueItem {
  address: AddressRecord;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: ShipmentResult;
  error?: string;
  selectedRate?: Rate;
  previewShipmentId?: string;
  insurance?: InsuranceTerms | null;
}

interface StoredBulkQueueItem {
  address: AddressRecord;
  selectedRate?: Rate;
  previewShipmentId?: string;
  insurance?: InsuranceTerms | null;
}

interface StoredBulkQueue {
  version: 3;
  apiUrl: string;
  parcel: Parcel;
  fromAddress: Address | null;
  items: StoredBulkQueueItem[];
}

interface LegacyStoredBulkQueue {
  version: 2;
  parcel: Parcel;
  fromAddress: Address | null;
  items: StoredBulkQueueItem[];
}

export function persistBulkQueue(
  items: ShipmentQueueItem[],
  parcel: Parcel,
  fromAddress: Address | null,
  apiUrl: string
): boolean {
  try {
    const remaining = items.filter((item) => item.status !== 'completed');
    if (remaining.length === 0) {
      sessionStorage.removeItem('bulkShipAddresses');
      return true;
    }

    const payload: StoredBulkQueue = {
      version: 3,
      apiUrl: canonicalizeRetryApiIdentity(apiUrl),
      parcel,
      fromAddress,
      items: remaining.map((item) => ({
        address: item.address,
        selectedRate: item.selectedRate,
        previewShipmentId: item.previewShipmentId,
        insurance: item.insurance,
      })),
    };
    sessionStorage.setItem('bulkShipAddresses', JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

function clearBulkQueue(): void {
  try {
    sessionStorage.removeItem('bulkShipAddresses');
  } catch {
    // Nothing else can be done when browser storage is unavailable.
  }
}

function BulkShipPage() {
  const { toasts, removeToast, success, error } = useToast();
  const navigate = useNavigate();

  // Addresses from sessionStorage
  const [queueItems, setQueueItems] = useState<ShipmentQueueItem[]>([]);
  const [fromAddress, setFromAddress] = useState<Address | null>(null);
  const [parcel, setParcel] = useState<Parcel>(emptyParcel);
  const [configuredInsurance, setConfiguredInsurance] = useState<InsuranceTerms | null>(null);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Loading
  const [loadingSettings, setLoadingSettings] = useState(true);
  const queueAbortRef = useRef<AbortController | null>(null);
  const unmountedRef = useRef(false);
  const persistedSenderRef = useRef<Address | null>(null);
  const queueApiUrlRef = useRef(
    canonicalizeRetryApiIdentity(getRuntimeApiConfig()?.apiUrl || '')
  );

  useBillableNavigationLock(isProcessing);

  useEffect(() => {
    unmountedRef.current = false;
    // Load addresses from sessionStorage
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem('bulkShipAddresses');
    } catch {
      error('Queue Unavailable', 'Browser session storage is unavailable. Select the addresses again.');
    }
    let restoredQueue = false;
    if (stored) {
      try {
        const parsed: AddressRecord[] | StoredBulkQueue | LegacyStoredBulkQueue = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          queueApiUrlRef.current = canonicalizeRetryApiIdentity(
            getRuntimeApiConfig()?.apiUrl || ''
          );
          setQueueItems(parsed.map((address) => ({ address, status: 'pending' })));
          restoredQueue = true;
        } else if (
          !Array.isArray(parsed) &&
          (parsed?.version === 2 || parsed?.version === 3) &&
          Array.isArray(parsed.items) &&
          parsed.items.length > 0
        ) {
          const hasPreparedPreview = parsed.items.some((item) => Boolean(item.previewShipmentId));
          queueApiUrlRef.current = resolveStoredBulkApiIdentity(
            parsed,
            getRuntimeApiConfig()?.apiUrl || ''
          );
          setQueueItems(parsed.items.map((item) => ({
            address: item.address,
            selectedRate: item.selectedRate,
            previewShipmentId: item.previewShipmentId,
            insurance: item.insurance,
            status: 'pending',
          })));
          if (parsed.parcel && typeof parsed.parcel === 'object') setParcel(parsed.parcel);
          const preparedInsurance = parsed.items.find((item) => item.insurance)?.insurance;
          if (preparedInsurance) setConfiguredInsurance(preparedInsurance);
          // The original sender is immutable once a preview exists. Before
          // that point, reload Settings so an interrupted unprepared queue
          // does not silently use stale sender details.
          if (hasPreparedPreview && parsed.fromAddress) {
            persistedSenderRef.current = parsed.fromAddress;
            setFromAddress(parsed.fromAddress);
          }
          restoredQueue = true;
        } else {
          navigate('/addresses');
        }
      } catch {
        navigate('/addresses');
      }
    } else {
      navigate('/addresses');
    }

    if (restoredQueue && persistedSenderRef.current) {
      setLoadingSettings(false);
    } else if (restoredQueue) {
      loadSettings();
    }

    return () => {
      unmountedRef.current = true;
      queueAbortRef.current?.abort();
    };
  }, [navigate]);

  const loadSettings = async () => {
    try {
      const settings = await settingsApi.get();
      if (settings.sender_address) {
        setFromAddress({
          name: settings.sender_address.name,
          company: settings.sender_address.company,
          street1: settings.sender_address.street1,
          street2: settings.sender_address.street2,
          city: settings.sender_address.city,
          state: settings.sender_address.state,
          zip: settings.sender_address.zip,
          country: settings.sender_address.country,
          phone: settings.sender_address.phone,
          email: settings.sender_address.email,
        });
      }
      const insuranceAmount = settings.shipping_options?.default_insurance_amount;
      setConfiguredInsurance(
        settings.shipping_options?.insurance_enabled && Number(insuranceAmount) > 0
          ? { amount: Number(insuranceAmount).toFixed(2), currency: 'USD' }
          : null
      );
    } catch (err) {
      console.error('Failed to load settings:', err);
      const message = err instanceof Error ? err.message : 'Failed to load sender settings';
      error('Sender Settings Unavailable', message);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleRemoveAddress = (index: number) => {
    const item = queueItems[index];
    if (
      item?.previewShipmentId &&
      !window.confirm(
        'This prepared shipment may already have been purchased. Discarding it removes the safe retry handle and can make a later replacement purchase a duplicate label. Discard it anyway?'
      )
    ) {
      return;
    }
    setQueueItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      persistBulkQueue(next, parcel, fromAddress, queueApiUrlRef.current);
      return next;
    });
  };

  const processNextShipment = async (index: number, items: ShipmentQueueItem[]) => {
    const controller = queueAbortRef.current;
    if (!controller || controller.signal.aborted || unmountedRef.current) return;

    if (index >= items.length) {
      setIsProcessing(false);
      const completed = items.filter((item) => item.status === 'completed').length;
      const failed = items.filter((item) => item.status === 'failed').length;
      if (failed === 0) {
        clearBulkQueue();
      } else {
        persistBulkQueue(items, parcel, fromAddress, queueApiUrlRef.current);
      }
      success('Bulk Shipping Complete', `${completed} successful, ${failed} failed.`);
      return;
    }

    setCurrentIndex(index);
    let workingItem: ShipmentQueueItem = { ...items[index], status: 'processing' };
    let workingItems = items.map((item, itemIndex) =>
      itemIndex === index ? workingItem : item
    );

    // Update status to processing
    setQueueItems(workingItems);

    try {
      let previewShipmentId = workingItem.previewShipmentId;
      let cheapestRate = workingItem.selectedRate;

      if (!previewShipmentId || !cheapestRate) {
        // Get rates for this shipment
        const previewController = new AbortController();
        let previewTimedOut = false;
        const abortPreview = () => previewController.abort();
        controller.signal.addEventListener('abort', abortPreview, { once: true });
        if (controller.signal.aborted) previewController.abort();
        const previewTimeout = window.setTimeout(() => {
          previewTimedOut = true;
          previewController.abort();
        }, 30000);
        let preview: ShipmentPreview;
        try {
          preview = await shipmentsApi.preview({
            to_address: workingItem.address,
            from_address: fromAddress!,
            parcel,
          }, previewController.signal);
        } catch (err) {
          if (previewTimedOut) throw new Error('Rate lookup timed out. No purchase was attempted.');
          throw err;
        } finally {
          window.clearTimeout(previewTimeout);
          controller.signal.removeEventListener('abort', abortPreview);
        }

        // Match every FedEx carrier variant (FedEx, FedExDefault, …), not just
        // an exact "FedEx".
        const allowedRates = (preview.rates || []).filter(
          (rate) => rate.carrier.toUpperCase().startsWith('FEDEX')
        );
        if (allowedRates.length === 0) {
          throw new Error('No FedEx shipping rates available');
        }

        cheapestRate = allowedRates.reduce((min, rate) =>
          parseFloat(rate.rate) < parseFloat(min.rate) ? rate : min
        );
        previewShipmentId = preview.shipment_id;
        workingItem = {
          ...workingItem,
          previewShipmentId,
          selectedRate: cheapestRate,
          insurance: preview.insurance,
        };
        workingItems = workingItems.map((item, itemIndex) =>
          itemIndex === index ? workingItem : item
        );
        setQueueItems(workingItems);
      }

      if (cheapestRate.carrier.toUpperCase() !== 'FEDEX') {
        throw new Error('The saved rate is not an allowed FedEx rate');
      }

      // The preview ID and rate must be durable before the billable request. If
      // the response is interrupted, the same immutable preview is retried.
      if (!persistBulkQueue(workingItems, parcel, fromAddress, queueApiUrlRef.current)) {
        throw new Error('The queue could not be saved, so no label was purchased. Enable session storage and try again.');
      }

      // Purchase the shipment with the cheapest rate
      const purchaseController = new AbortController();
      let purchaseTimedOut = false;
      const abortPurchase = () => purchaseController.abort();
      controller.signal.addEventListener('abort', abortPurchase, { once: true });
      if (controller.signal.aborted) purchaseController.abort();
      const purchaseTimeout = window.setTimeout(() => {
        purchaseTimedOut = true;
        purchaseController.abort();
      }, 60000);
      let result: ShipmentResult;
      try {
        result = await shipmentsApi.confirm({
          to_address: workingItem.address,
          from_address: fromAddress!,
          parcel,
          confirm: true,
          confirmed_details: true,
          preview_shipment_id: previewShipmentId,
          rate_id: cheapestRate.id,
          accepted_rate_id: cheapestRate.id,
          accepted_rate_amount: cheapestRate.rate,
          accepted_rate_currency: cheapestRate.currency,
          ...(workingItem.insurance
            ? {
                accepted_insurance_amount: workingItem.insurance.amount,
                accepted_insurance_currency: workingItem.insurance.currency,
              }
            : {}),
        }, purchaseController.signal);
      } catch (err) {
        if (purchaseTimedOut) {
          throw new Error('Purchase timed out. The prepared shipment was kept; use Retry Failed Safely to recover the same purchase.');
        }
        throw err;
      } finally {
        window.clearTimeout(purchaseTimeout);
        controller.signal.removeEventListener('abort', abortPurchase);
      }

      if (controller.signal.aborted || unmountedRef.current) return;

      // Update with success
      const updatedItems = workingItems.map((it, i) =>
        i === index ? { ...it, status: 'completed' as const, result, selectedRate: cheapestRate } : it
      );
      setQueueItems(updatedItems);
      saveRecentOrder(result, workingItem.address, cheapestRate);
      persistBulkQueue(updatedItems, parcel, fromAddress, queueApiUrlRef.current);

      // Rate limit delay before processing next shipment
      if (index + 1 < items.length) {
        await delay(RATE_LIMIT_DELAY_MS, controller.signal);
      }

      // Process next
      await processNextShipment(index + 1, updatedItems);
    } catch (err) {
      if (controller.signal.aborted || unmountedRef.current || (err instanceof DOMException && err.name === 'AbortError')) {
        return;
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to create shipment';

      // Update with failure
      const updatedItems = workingItems.map((it, i) =>
        i === index ? { ...it, status: 'failed' as const, error: errorMessage } : it
      );
      setQueueItems(updatedItems);
      persistBulkQueue(updatedItems, parcel, fromAddress, queueApiUrlRef.current);

      // Rate limit delay before processing next shipment
      if (index + 1 < items.length) {
        try {
          await delay(RATE_LIMIT_DELAY_MS, controller.signal);
        } catch {
          return;
        }
      }

      // Continue with next
      await processNextShipment(index + 1, updatedItems);
    }
  };

  const handleStartProcessing = () => {
    const currentApiUrl = canonicalizeRetryApiIdentity(
      getRuntimeApiConfig()?.apiUrl || ''
    );
    const hasPreparedQueue = queueItems.some(
      (item) => item.status !== 'completed' && Boolean(item.previewShipmentId)
    );
    if (hasPreparedQueue) {
      if (!retryApiIdentityMatches(queueApiUrlRef.current, currentApiUrl)) {
        error(
          queueApiUrlRef.current === LEGACY_UNBOUND_API_IDENTITY
            ? 'Legacy Retry Needs Its Original Worker'
            : 'Worker Connection Mismatch',
          queueApiUrlRef.current === LEGACY_UNBOUND_API_IDENTITY
            ? 'This older prepared queue does not record which Worker created it. It has been preserved and will not be sent to an unverified Worker.'
            : `This prepared bulk queue belongs to ${queueApiUrlRef.current}. Reconnect to that Worker before retrying it.`
        );
        return;
      }
    } else {
      queueApiUrlRef.current = currentApiUrl;
    }

    if (
      !fromAddress?.name ||
      !fromAddress.street1 ||
      !fromAddress.city ||
      !fromAddress.state ||
      !fromAddress.zip
    ) {
      error('Missing Sender', 'Please configure sender address in Settings.');
      return;
    }

    if (!parcel.weight || parcel.weight <= 0) {
      error('Missing Weight', 'Please enter the package weight.');
      return;
    }

    if (queueItems.length === 0) {
      error('No Addresses', 'No addresses selected for shipping.');
      return;
    }

    const senderCountry = fromAddress.country || 'US';
    const crossBorderCount = queueItems.filter(
      (item) => (item.address.country || 'US') !== senderCountry
    ).length;
    if (crossBorderCount > 0) {
      error(
        'Customs Information Required',
        `${crossBorderCount} selected address${crossBorderCount === 1 ? '' : 'es'} require cross-border customs data and cannot be processed.`
      );
      return;
    }

    setIsProcessing(true);
    if (!persistBulkQueue(queueItems, parcel, fromAddress, queueApiUrlRef.current)) {
      setIsProcessing(false);
      error('Queue Storage Unavailable', 'No labels were purchased. Enable session storage and try again.');
      return;
    }
    queueAbortRef.current?.abort();
    queueAbortRef.current = new AbortController();
    processNextShipment(0, queueItems);
  };

  const handleGoBack = () => {
    const hasRecoveryData = hasPreparedRecovery || failedCount > 0;
    if (
      hasRecoveryData &&
      !window.confirm(
        'Discard the saved retry information? A prepared shipment may already have been purchased, and losing its preview ID can make a later replacement purchase a duplicate label.'
      )
    ) {
      return;
    }
    clearBulkQueue();
    navigate('/addresses');
  };

  const handleRetryFailed = () => {
    const retryItems = queueItems
      .filter((item) => item.status === 'failed')
      .map((item) => ({ ...item, status: 'pending' as const, error: undefined }));
    if (retryItems.length === 0) return;
    const currentApiUrl = canonicalizeRetryApiIdentity(
      getRuntimeApiConfig()?.apiUrl || ''
    );
    const hasPreparedRetry = retryItems.some((item) => Boolean(item.previewShipmentId));
    if (
      hasPreparedRetry &&
      !retryApiIdentityMatches(queueApiUrlRef.current, currentApiUrl)
    ) {
      error(
        queueApiUrlRef.current === LEGACY_UNBOUND_API_IDENTITY
          ? 'Legacy Retry Needs Its Original Worker'
          : 'Worker Connection Mismatch',
        queueApiUrlRef.current === LEGACY_UNBOUND_API_IDENTITY
          ? 'This older prepared queue does not record which Worker created it. It has been preserved and will not be sent to an unverified Worker.'
          : `This prepared bulk queue belongs to ${queueApiUrlRef.current}. Reconnect to that Worker before retrying it.`
      );
      return;
    }
    if (!hasPreparedRetry) queueApiUrlRef.current = currentApiUrl;
    if (!persistBulkQueue(retryItems, parcel, fromAddress, queueApiUrlRef.current)) {
      error('Queue Storage Unavailable', 'No labels were purchased. Enable session storage and try again.');
      return;
    }

    setQueueItems(retryItems);
    setCurrentIndex(0);
    setIsProcessing(true);
    queueAbortRef.current?.abort();
    queueAbortRef.current = new AbortController();
    processNextShipment(0, retryItems);
  };

  const completedCount = queueItems.filter((item) => item.status === 'completed').length;
  const failedCount = queueItems.filter((item) => item.status === 'failed').length;
  const pendingCount = queueItems.filter((item) => item.status === 'pending').length;
  const hasPreparedRecovery = queueItems.some(
    (item) => item.status !== 'completed' && Boolean(item.previewShipmentId)
  );

  const allDone = queueItems.length > 0 && pendingCount === 0 && !isProcessing;

  if (loadingSettings) {
    return (
      <div className="panel">
        <div className="skeleton" style={{ height: '200px' }} />
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="intro-box">
        <strong>Bulk Shipping</strong>
        <p>
          Create shipments for {queueItems.length} selected address{queueItems.length !== 1 ? 'es' : ''}.
          Set the package weight below, then start processing to create labels for all addresses.
        </p>
      </div>

      <div style={{ display: 'grid', gap: '30px' }}>
        {/* Parcel Settings */}
        {!allDone && (
          <div className="panel">
            <h2>Package Details</h2>
            <p style={{ marginBottom: '16px', color: 'var(--aged-mahogany-light)' }}>
              These settings will be used for all shipments.
            </p>
            <ParcelForm parcel={parcel} onChange={setParcel} readOnly={isProcessing || hasPreparedRecovery} />
            {configuredInsurance && (
              <p
                style={{
                  background: 'var(--info-bg)',
                  color: 'var(--info-text)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '12px 16px',
                  marginTop: '16px',
                }}
              >
                Each shipment will have an insured value of {formatCurrency(configuredInsurance.amount, configuredInsurance.currency)}.
                {' '}EasyPost charges an additional insurance fee of 1% of the insured value per shipment, with a $1.00 minimum.
              </p>
            )}
            {hasPreparedRecovery && !isProcessing && (
              <p className="field-hint">
                {queueApiUrlRef.current === LEGACY_UNBOUND_API_IDENTITY
                  ? 'This legacy prepared queue is preserved but blocked because it does not identify its original Worker. Do not discard it unless you have verified the earlier purchase state.'
                  : `Package details are locked because this queue contains a prepared shipment awaiting confirmation through ${queueApiUrlRef.current}.`}
              </p>
            )}

            <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
              <button
                className={`btn btn-primary btn-large ${isProcessing ? 'loading' : ''}`}
                onClick={handleStartProcessing}
                disabled={isProcessing || queueItems.length === 0}
              >
                {isProcessing
                  ? `Processing ${currentIndex + 1} of ${queueItems.length}...`
                  : `Create ${queueItems.length} Shipment${queueItems.length !== 1 ? 's' : ''}`}
              </button>
              <button
                className="btn btn-ghost"
                onClick={handleGoBack}
                disabled={isProcessing}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Progress Summary */}
        {(isProcessing || allDone) && (
          <div className="panel">
            <h2>Progress</h2>
            <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
              <div>
                <span style={{ fontWeight: 600, color: 'var(--success-text)' }}>{completedCount}</span>
                <span style={{ marginLeft: '4px', color: 'var(--aged-mahogany-light)' }}>Completed</span>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'var(--error-text)' }}>{failedCount}</span>
                <span style={{ marginLeft: '4px', color: 'var(--aged-mahogany-light)' }}>Failed</span>
              </div>
              <div>
                <span style={{ fontWeight: 600 }}>{pendingCount}</span>
                <span style={{ marginLeft: '4px', color: 'var(--aged-mahogany-light)' }}>Pending</span>
              </div>
            </div>

            {allDone && (
              <div style={{ display: 'flex', gap: '12px' }}>
                {failedCount > 0 && (
                  <button className="btn btn-primary" onClick={handleRetryFailed}>
                    Retry Failed Safely
                  </button>
                )}
                <button className="btn btn-primary" onClick={() => navigate('/orders')}>
                  View Order History
                </button>
                <button className="btn btn-ghost" onClick={handleGoBack}>
                  {failedCount > 0 ? 'Discard Failed & Back' : 'Back to Addresses'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Address Queue */}
        <div className="panel">
          <h2>
            Shipping Queue
            <span className="count-pill" style={{ marginLeft: '12px' }}>
              {queueItems.length}
            </span>
          </h2>

          {queueItems.length === 0 ? (
            <div className="empty-state">
              <p>No addresses in queue.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Status</th>
                    <th>Result</th>
                    {!isProcessing && !allDone && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {queueItems.map((item, index) => (
                    <tr key={item.address.id}>
                      <td data-label="Name">
                        <strong>{item.address.name}</strong>
                        {item.address.company && (
                          <div style={{ fontSize: '0.85em', color: 'var(--aged-mahogany-light)' }}>
                            {item.address.company}
                          </div>
                        )}
                      </td>
                      <td data-label="Address">
                        {item.address.city}, {item.address.state} {item.address.zip}
                        {item.address.country && item.address.country !== 'US' ? ` ${item.address.country}` : ''}
                      </td>
                      <td data-label="Status">
                        {item.status === 'pending' && (
                          <span style={{ color: 'var(--aged-mahogany-light)' }}>Pending</span>
                        )}
                        {item.status === 'processing' && (
                          <span style={{ color: 'var(--info-text)', fontWeight: 500 }}>Processing...</span>
                        )}
                        {item.status === 'completed' && (
                          <span style={{ color: 'var(--success-text)', fontWeight: 500 }}>Completed</span>
                        )}
                        {item.status === 'failed' && (
                          <span style={{ color: 'var(--error-text)', fontWeight: 500 }}>Failed</span>
                        )}
                      </td>
                      <td data-label="Result">
                        {item.status === 'completed' && item.result && (
                          <div>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                              {item.result.tracking_code}
                            </div>
                            {item.selectedRate && (
                              <div style={{ fontSize: '0.85em', color: 'var(--aged-mahogany-light)' }}>
                                {item.selectedRate.carrier} - {formatCurrency(item.selectedRate.rate, item.selectedRate.currency)}
                              </div>
                            )}
                            {item.result.label_url && (
                              <a
                                href={buildPrintPath(item.result.label_url, item.result.label_file_type)}
                                style={{ fontSize: '0.85em' }}
                              >
                                Print Label
                              </a>
                            )}
                          </div>
                        )}
                        {item.status === 'failed' && item.error && (
                          <span style={{ color: 'var(--error-text)', fontSize: '0.9em' }}>
                            {item.error}
                          </span>
                        )}
                      </td>
                      {!isProcessing && !allDone && (
                        <td data-label="Actions">
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            onClick={() => handleRemoveAddress(index)}
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

    </>
  );
}

export default BulkShipPage;
